<template>
  <div class="calculator">
    <div v-for="(loan, idx) in loans" :key="loan.id" class="loan-section">
      <div class="loan-header">
        <!-- Replace static heading with editable loan name input -->
        <h2 v-if="loans.length > 1">
          <input v-model="loan.loanName" class="loan-name-input" type="text" :aria-label="`Name for loan ${idx + 1}`" />
          <button v-if="loans.length > 1" class="remove-cross" @click="removeLoan(idx)" title="Remove loan"
            type="button">&times;</button>
        </h2>
        <h2 v-else>
          <input v-model="loan.loanName" class="loan-name-input single" type="text" aria-label="Loan name" />
        </h2>
      </div>

      <div class="loan-content">
        <div class="loan-inputs">
          <!-- Loan amount (inline) -->
          <div class="form-row inline" :class="{ 'readonly-group': idx > 0 }">
            <label>Loan amount:</label>
            <input :id="`loan-amt-${loan.id}`" :class="['numeric', { readonly: idx > 0 }]" type="number"
              v-model.number="loan.loanAmount" min="0" step="0.01" :placeholder="idx === 0 ? 'Enter amount' : ''"
              :readonly="idx > 0" :aria-readonly="idx > 0 ? 'true' : 'false'"
              :aria-describedby="idx > 0 ? `tip-${loan.id}` : null" :title="idx === 0 ? 'Enter starting amount' : ''" />
            <span v-if="idx > 0" class="calc-tooltip" :id="`tip-${loan.id}`" role="tooltip">
              This number is calculated from the remaining loan of the previous loan.
            </span>
          </div>
          <!-- Interest rate (inline) -->
          <div class="form-row inline">
            <label>Interest rate (%):</label>
            <input class="numeric" type="number" v-model.number="loan.interestRate" min="0" max="100" step="0.01"
              placeholder="Rate" />
          </div>
          <!-- Term length slider -->
          <div class="form-row">
            <label>Term length (years): <strong>{{ loan.termLength }}</strong></label>
            <input class="full-range" type="range" min="0.5" max="30" step="0.5" v-model.number="loan.termLength"
              @input="clampTerm(idx)" />
          </div>
          <!-- Loan length slider -->
          <div class="form-row">
            <label>Fixed length (years): <strong>{{ loan.loanLength }}</strong></label>
            <input class="full-range" type="range" min="0.5" :max="loan.termLength" step="0.5"
              v-model.number="loan.loanLength" />
          </div>
          <!-- Frequency -->
          <div class="form-row">
            <!-- <label>Payment frequency</label> -->
            <div class="button-row frequency-buttons compact">
              <button :class="{ selected: loan.paymentFrequency === 'monthly' }" @click="setFrequency(idx, 'monthly')"
                type="button">Monthly</button>
              <button :class="{ selected: loan.paymentFrequency === 'fortnightly' }"
                @click="setFrequency(idx, 'fortnightly')" type="button">Fortnightly</button>
            </div>
          </div>
          <!-- Payment type -->
          <div class="form-row">
            <!-- <label>Payment type</label> -->
            <div class="button-row type-buttons compact">
              <button :class="{ selected: loan.paymentType === 'principal' }" @click="setPaymentType(idx, 'principal')"
                type="button">Principal + Interest</button>
              <button :class="{ selected: loan.paymentType === 'interestOnly' }"
                @click="setPaymentType(idx, 'interestOnly')" type="button">Interest Only</button>
            </div>
          </div>
        </div>

        <div class="loan-results">
          <p><strong>Starting amount:</strong> {{ formatCurrency(loan.loanAmount) }}</p>
          <p><span class="label">Payment / {{ loan.paymentFrequency === 'monthly' ? 'month' : 'fortnight' }}:</span>
            <strong>{{ formatCurrency(loanResults[idx]?.payment) }}</strong>
          </p>
          <p><span class="label">Total Interest:</span> {{ formatCurrency(loanResults[idx]?.interestPaid) }}</p>
          <p><span class="label">Total Principal:</span> {{ formatCurrency(loanResults[idx]?.principalPaid) }}</p>
          <p><span class="label">Total Paid:</span> {{ formatCurrency(loanResults[idx]?.totalPaid) }}</p>
          <p class="remaining"><span class="label">Remaining loan:</span> <strong>{{
            formatCurrency(loanResults[idx]?.remaining) }}</strong></p>
        </div>
      </div>

      <!-- Lump sum section shown only if there is a subsequent loan -->
      <div v-if="idx < loans.length - 1" class="between-loans">
        <div class="form-row inline lump-sum-row">
          <label class="label-with-icon">
            Lump sum before next:
            <span class="help-icon" tabindex="0" aria-label="More information">?
              <span class="tooltip-popup">
                An optional one-off payment made to reduce the loan principal before the next loan begins.
              </span>
            </span>
          </label>
          <input class="numeric" type="number" min="0" step="0.01" v-model.number="loan.lumpSumToNext"
            @input="validateLumpSum(idx)" :max="loanResults[idx]?.remaining" />
        </div>
        <small v-if="loan.lumpSumToNext > (loanResults[idx]?.remaining || 0)" class="helper">Value exceeds remaining –
          will be capped.</small>
        <hr />
      </div>

      <!-- Add next loan button only on last loan -->
      <div v-if="idx === loans.length - 1" class="add-next-wrapper">
        <button class="add-next-loan" @click="addNextLoan(idx)">Add Next Loan</button>
      </div>
    </div>
  </div>
</template>

<script>
import { calculatePayment } from '../utils/calculations.js';

export default {
  name: 'LoanCalculator',
  data() {
    const initialLoan = { id: 1, loanName: 'Loan 1', loanAmount: 0, interestRate: 5, loanLength: 2, termLength: 30, paymentFrequency: 'monthly', paymentType: 'principal', lumpSumToNext: 0 };
    return { nextLoanId: 2, loans: [initialLoan] };
  },
  emits: ['summary'],
  computed: {
    loanResults() {
      return this.loans.map(l => calculatePayment(
        l.loanAmount,
        l.interestRate,
        l.termLength,
        l.loanLength,
        l.paymentFrequency,
        l.paymentType === 'interestOnly'
      ));
    },
    summaryLoans() {
      return this.loans.map((loan, idx) => ({
        ...loan,
        lumpSumToNext: loan.lumpSumToNext,
        payment: this.loanResults[idx].payment,
        interestPaid: this.loanResults[idx].interestPaid,
        principalPaid: this.loanResults[idx].principalPaid,
        totalPaid: this.loanResults[idx].totalPaid,
        remaining: this.loanResults[idx].remaining
      }));
    }
  },
  watch: {
    summaryLoans: {
      immediate: true,
      handler(newVal) {
        for (let i = 1; i < newVal.length; i++) {
          const prevRemaining = Number(newVal[i - 1].remaining.toFixed(2));
          const prevLoan = this.loans[i - 1];
          if (prevLoan.lumpSumToNext == null) prevLoan.lumpSumToNext = 0;
          if (prevLoan.lumpSumToNext > prevRemaining) prevLoan.lumpSumToNext = prevRemaining; // clamp
          const adjusted = Number((prevRemaining - (prevLoan.lumpSumToNext || 0)).toFixed(2));
          if (this.loans[i].loanAmount !== adjusted) this.loans[i].loanAmount = adjusted;
        }
        this.$emit('summary', newVal);
      }
    }
  },
  methods: {
    createLoan(overrides = {}) {
      const id = this.nextLoanId++;
      return Object.assign({
        id,
        loanName: `Loan ${id}`,
        loanAmount: 0,
        interestRate: 5,
        loanLength: 2,
        termLength: 30,
        paymentFrequency: 'monthly',
        paymentType: 'principal',
        lumpSumToNext: 0
      }, overrides);
    },
    setFrequency(idx, freq) { this.loans[idx].paymentFrequency = freq; },
    setPaymentType(idx, type) { this.loans[idx].paymentType = type; },
    formatCurrency(value) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0); },
    addNextLoan(idx) {
      const prevRemaining = this.loanResults[idx].remaining;
      const prevLoan = this.loans[idx];
      const adjusted = Math.max(0, Number((prevRemaining - (prevLoan.lumpSumToNext || 0)).toFixed(2)));
      this.loans.push(this.createLoan({ loanAmount: adjusted }));
    },
    removeLoan(idx) { if (this.loans.length > 1) this.loans.splice(idx, 1); },
    clampTerm(idx) {
      const loan = this.loans[idx];
      if (loan.loanLength > loan.termLength) loan.loanLength = loan.termLength;
    },
    validateLumpSum(idx) {
      const loan = this.loans[idx];
      if (loan.lumpSumToNext == null || isNaN(loan.lumpSumToNext)) loan.lumpSumToNext = 0;
      if (loan.lumpSumToNext < 0) loan.lumpSumToNext = 0;
      const remaining = this.loanResults[idx]?.remaining || 0;
      if (loan.lumpSumToNext > remaining) loan.lumpSumToNext = Number(remaining.toFixed(2));
      const next = this.loans[idx + 1];
      if (next) {
        const adjusted = Math.max(0, Number((remaining - loan.lumpSumToNext).toFixed(2)));
        if (next.loanAmount !== adjusted) next.loanAmount = adjusted;
      }
    }
  }
}
</script>

<style scoped>
/* --- Modern layout with cards and smooth transitions --- */
.calculator {
  max-width: 100%;
  overflow-x: hidden;
}

.loan-section {
  margin-bottom: var(--space-xl);
  padding: var(--space-xl) 0;
  background: var(--color-bg-white);
  border-radius: var(--radius-xl);
  transition: all var(--transition-normal);
}

.loan-section:not(:last-child) {
  border-bottom: 2px solid var(--color-border);
  padding-bottom: var(--space-xl);
  margin-bottom: var(--space-xl);
}

.loan-header {
  margin-bottom: var(--space-lg);
}

.loan-header h2 {
  text-align: left;
  margin: 0;
}

.loan-content {
  display: flex;
  gap: var(--space-lg);
  align-items: flex-start;
}

.loan-inputs {
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
}

.loan-results {
  flex: 1;
  min-width: 0;
  background: var(--color-primary-light);
  padding: var(--space-lg);
  border: none;
  border-radius: var(--radius-xl);
  font-size: 0.95rem;
  overflow-wrap: break-word;
  word-wrap: break-word;
  box-sizing: border-box;
  color: var(--color-text);
  box-shadow: 0 4px 12px rgba(29, 59, 70, 0.2);
}

.loan-results p {
  margin: 0.75rem 0;
  display: flex;
  justify-content: space-between;
  gap: var(--space-sm);
  align-items: flex-start;
}

.loan-results p strong,
.loan-results p>span:last-child {
  text-align: left;
  flex-shrink: 0;
}

.loan-results p.remaining {
  margin-top: var(--space-md);
  border-top: 1px solid rgba(23, 54, 65, 0.2);
  padding-top: var(--space-md);
  font-size: 1.1rem;
}

.loan-results .label {
  color: var(--color-text-light);
  font-weight: 500;
}

.form-row {
  display: flex;
  flex-direction: column;
  margin-top: .85rem;
}

.form-row label {
  font-weight: 600;
  margin-bottom: var(--space-sm);
  text-align: left;
  color: var(--color-primary);
  font-size: 0.9rem;
}

.form-row:first-of-type {
  margin-top: 0;
}

.full-range {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: var(--color-border);
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

.full-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: var(--radius-full);
  background: var(--color-primary);
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all var(--transition-fast);
}

.full-range::-webkit-slider-thumb:hover {
  transform: scale(1.2);
}

.full-range::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: var(--radius-full);
  background: var(--color-primary);
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all var(--transition-fast);
}

.full-range::-moz-range-thumb:hover {
  transform: scale(1.2);
}

.button-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
}

/* Center specific button groups */
.frequency-buttons.compact,
.type-buttons.compact {
  justify-content: flex-start;
}

.frequency-buttons button,
.type-buttons button {
  margin: 0;
}

.add-next-wrapper {
  display: flex;
  justify-content: center;
  margin-top: var(--space-md);
}

.add-next-loan {
  margin-top: 0;
  padding: 0.75rem 2rem;
  background: var(--color-secondary);
  color: var(--color-white);
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  transition: all var(--transition-smooth);
  box-shadow: var(--shadow-secondary);
}

.add-next-loan:hover {
  transform: translateY(-2px);
  background: #829FA1;
  /* Lighter sage */
  box-shadow: 0 6px 16px rgba(110, 139, 141, 0.4);
}

.add-next-loan:active {
  transform: translateY(0);
}

.frequency-buttons button {
  border: 2px solid var(--color-primary);
  background: var(--color-bg-white);
  color: var(--color-primary);
  cursor: pointer;
  border-radius: var(--radius-md);
  padding: 0.6rem 1.2rem;
  font-weight: 600;
  transition: all var(--transition-fast);
}

.frequency-buttons button.selected {
  background: var(--color-primary);
  color: var(--color-bg-white);
  box-shadow: var(--shadow-primary);
}

.frequency-buttons button:hover:not(.selected) {
  background: var(--color-primary-light);
  transform: translateY(-1px);
}

.type-buttons {
  margin-top: 6px;
}

.type-buttons button {
  border: 2px solid var(--color-primary);
  background: var(--color-bg-white);
  color: var(--color-primary);
  cursor: pointer;
  border-radius: var(--radius-md);
  padding: 0.6rem 1.2rem;
  font-weight: 600;
  transition: all var(--transition-fast);
}

.type-buttons button.selected {
  background: var(--color-primary);
  color: var(--color-bg-white);
  box-shadow: var(--shadow-primary);
}

.type-buttons button:hover:not(.selected) {
  background: var(--color-primary-light);
  transform: translateY(-1px);
}

.remove-cross {
  background: none;
  border: none;
  color: var(--color-error);
  font-size: 1.3em;
  cursor: pointer;
  margin-left: 12px;
  padding: 4px 8px;
  border-radius: var(--radius-full);
  vertical-align: middle;
  transition: all var(--transition-fast);
}

.remove-cross:hover {
  background: var(--color-error-light);
  transform: scale(1.1);
}

/* --- new styles for inline inputs and right alignment --- */
.form-row.inline {
  flex-direction: row;
  align-items: center;
}

.form-row.inline label {
  width: 145px;
  margin-bottom: 0;
  font-weight: 600;
  text-align: left;
}

.form-row.inline input {
  flex: 1;
}

.numeric {
  text-align: right;
  padding: 0.6rem 0.8rem;
  border: 2px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.95rem;
  transition: all var(--transition-fast);
}

.numeric:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: var(--shadow-accent);
}

.numeric.narrow {
  max-width: 140px;
}

.loan-name-input {
  font-size: 1.25rem;
  font-weight: 700;
  border: 2px solid var(--color-border);
  padding: var(--space-sm) 0.75rem;
  border-radius: var(--radius-md);
  min-width: 160px;
  margin-right: 12px;
  transition: all var(--transition-fast);
  color: var(--color-text);
  text-align: left;
}

.loan-name-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: var(--shadow-accent);
}

.loan-name-input.single {
  margin-right: 0;
}


.numeric.readonly {
  background: var(--color-bg-light);
  border-color: var(--color-border);
  color: var(--color-text-muted);
  cursor: not-allowed;
}


/* --- new styles for tooltip --- */
.readonly-group {
  position: relative;
}

.calc-tooltip {
  position: absolute;
  left: 145px;
  /* align under input start (label width) */
  top: 100%;
  margin-top: 6px;
  background: var(--color-text);
  color: var(--color-bg-white);
  padding: 8px 12px;
  font-size: 0.75rem;
  border-radius: var(--radius-sm);
  line-height: 1.4;
  max-width: 200px;
  box-shadow: var(--shadow-lg);
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity var(--transition-fast), transform var(--transition-fast);
  pointer-events: none;
  z-index: 10;
}

.readonly-group:hover .calc-tooltip,
.readonly-group:focus-within .calc-tooltip {
  opacity: 1;
  transform: translateY(0);
}

.calc-tooltip::after {
  content: '';
  position: absolute;
  top: -6px;
  left: 16px;
  width: 10px;
  height: 10px;
  background: var(--color-text);
  transform: rotate(45deg);
}

@media (max-width: 820px) {
  .loan-content {
    flex-direction: column;
  }

  .loan-inputs,
  .loan-results {
    width: 100%;
    max-width: 100%;
  }

  .add-next-wrapper {
    margin-top: 1.25rem;
  }

  .loan-section {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  /* Stack lump sum input */
  .form-row.inline.lump-sum-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-row.inline.lump-sum-row label {
    margin-bottom: var(--space-sm);
    margin-right: 0;
  }

  .form-row.inline.lump-sum-row input.numeric {
    width: 100%;
    box-sizing: border-box;
  }
}

/* --- new styles for lump sum payment feature --- */
.between-loans {
  background: var(--color-primary-light);
  border: 2px dashed var(--color-primary);
  padding: var(--space-md) 1.25rem;
  border-radius: var(--radius-lg);
  margin: var(--space-lg) 0;
}

.lump-sum-row label {
  width: auto;
  min-width: 145px;
  white-space: nowrap;
  margin-right: 3rem;
}

.lump-sum-row input.numeric {
  flex: 1;
  max-width: none;
}

.helper {
  display: block;
  margin-top: var(--space-sm);
  font-size: 0.75rem;
  color: var(--color-error);
  text-align: left;
  padding-left: 145px;
  font-weight: 500;
}

.between-loans hr {
  border: none;
  border-top: 1px solid rgba(29, 59, 70, 0.3);
  margin-top: var(--space-md);
}

/* --- Tooltip styles for lump sum --- */
.label-with-icon {
  display: flex;
  align-items: center;
}

.help-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 16px;
  height: 16px;
  background: #ccc;
  color: var(--color-bg-white);
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: bold;
  margin-left: 8px;
  cursor: help;
  position: relative;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.help-icon:hover,
.help-icon:focus {
  background: var(--color-accent);
  transform: scale(1.1);
}

.tooltip-popup {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  max-width: 250px;
  padding: 8px 12px;
  background: var(--color-text);
  color: var(--color-bg-white);
  font-size: 0.75rem;
  line-height: 1.4;
  border-radius: var(--radius-sm);
  text-align: left;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity var(--transition-fast), transform var(--transition-fast);
  z-index: 100;
  box-shadow: var(--shadow-lg);
  font-weight: normal;
  white-space: normal;
  width: max-content;
}

.tooltip-popup::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: var(--color-text) transparent transparent transparent;
}

.help-icon:hover .tooltip-popup,
.help-icon:focus .tooltip-popup {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}
</style>
